import {
    type DataFrame,
    type MessageCreatedFrame,
    type WsEvent,
} from 'src/services/websocket/ws-protocol';

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isDataFrame(value: unknown): value is DataFrame {
    return isRecord(value) && typeof value.type === 'string';
}

function hasOnlyKeys(frame: Record<string, unknown>, keys: readonly string[]): boolean {
    const allowed = new Set(keys);
    return Object.keys(frame).every((key) => allowed.has(key));
}

function parseAuthError(frame: Record<string, unknown>): WsEvent | null {
    if (!hasOnlyKeys(frame, ['type', 'reason'])) {
        return null;
    }
    if (frame.reason !== undefined && typeof frame.reason !== 'string') {
        return null;
    }
    return frame.reason === undefined ?
        { type: 'AUTH_ERROR' } :
        { type: 'AUTH_ERROR', reason: frame.reason };
}

function parseMessageCreated(frame: Record<string, unknown>): WsEvent | null {
    if (!hasOnlyKeys(frame, ['type', 'leasing_team_id', 'lead_uuid', 'ts'])) {
        return null;
    }
    if (
        typeof frame.leasing_team_id !== 'number' ||
        !Number.isFinite(frame.leasing_team_id) ||
        typeof frame.lead_uuid !== 'string' ||
        frame.lead_uuid.length === 0 ||
        typeof frame.ts !== 'number' ||
        !Number.isFinite(frame.ts)
    ) {
        return null;
    }
    const messageCreated: MessageCreatedFrame = {
        type: 'message.created',
        leasing_team_id: frame.leasing_team_id,
        lead_uuid: frame.lead_uuid,
        ts: frame.ts,
    };
    return { type: 'FRAME', frame: messageCreated };
}

/** Pure boundary parser for untrusted browser WebSocket message data. */
export function parseIncomingWsFrame(raw: unknown): WsEvent | null {
    if (typeof raw !== 'string') {
        return null;
    }

    let frame: unknown;
    try {
        frame = JSON.parse(raw);
    } catch {
        return null;
    }
    if (!isDataFrame(frame)) {
        return null;
    }

    switch (frame.type) {
        case 'auth_ok':
            return hasOnlyKeys(frame, ['type']) ? { type: 'AUTH_OK' } : null;
        case 'auth_error':
            return parseAuthError(frame);
        case 'message.created':
            return parseMessageCreated(frame);
        default:
            return { type: 'FRAME', frame };
    }
}
